import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  RiAddLine,
  RiAlertLine,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiLockLine,
} from '@remixicon/react';

import { Badge, StatusBadge } from '@/design-system/badge';
import { Banner } from '@/design-system/banner';
import { Button } from '@/design-system/button';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { FormModal } from '@/design-system/form-modal';
import { Skeleton } from '@/design-system/skeleton';
import { Tag } from '@/design-system/tag';
import { toast } from '@/design-system/toaster';
import { workflowPinProblemLabel } from '@/services/api/routePlans';
import {
  isChargedOverride,
  isPool,
  isStopCharged,
  stopKindLabel,
} from '@/services/api/routeStopKind';
import {
  parseRevenueNotes,
  ujpAmendmentErrorCode,
  UjpApiError,
  ujpAPI,
  type UjpAmendment,
  type UjpDetail,
  type UjpHistoryEntry,
  type UjpRouteStop,
  type UjpSpendRequestState,
  type UjpSpendRequestView,
} from '@/services/api/ujp';

import { UJP_COPY } from '../copy';
import {
  formatDate,
  formatDateTime,
  formatKm,
  formatMaskedAccount,
  formatRupiah,
  toNum,
} from '../format';
import ResendSpendRequestModal from './ResendSpendRequestModal';
import UjpAmendmentModal from './UjpAmendmentModal';
import {
  UjpAgeBadge,
  UjpAmendmentStatusBadge,
  UjpStatusBadge,
} from './UjpBadges';
import UjpDecisionModal, { type UjpDecisionMode } from './UjpDecisionModal';
import { UjpHiddenAmount, UjpTiles, usesQris } from './UjpTiles';

/**
 * The UJP detail panel (DD3): money first, then what approving will actually
 * create, then the numbers behind it, the bank account, the route and the
 * history. `FormModal position="right" size="md"` is this app's SideBarModal.
 *
 * Everything that gates an action comes from the server's `viewer` block
 * (`canDecide` / `canCancel` / `isRequester`) rather than the build-time
 * approver allowlist, so the panel and the endpoint can never disagree about
 * who may click "Setujui".
 */

// ─────────────────────────────────────────────────────────────────────────────
// Layout atoms
// ─────────────────────────────────────────────────────────────────────────────

const Section: React.FC<{
  title?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="border-b border-stroke-soft-200 px-5 py-4 last:border-b-0">
    {title && (
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-text-soft-400">
        {title}
      </h3>
    )}
    {children}
  </section>
);

const KV: React.FC<{
  k: React.ReactNode;
  sub?: React.ReactNode;
  children: React.ReactNode;
}> = ({ k, sub, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="min-w-0 text-text-sub-600">
      {k}
      {sub && <span className="block text-[11px] text-text-soft-400">{sub}</span>}
    </span>
    <span className="shrink-0 text-right font-semibold text-text-strong-950">
      {children}
    </span>
  </div>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-mono tabular-nums">{children}</span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Spend Control helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The "Belum ada alur approval di Spend Control" banner (SENT with
 * `workflowConfigured: false`) stays hidden for now: Basecamp does not report
 * back to Mithril yet (status callback, TODO-69), so ops cannot act on it. The
 * server still sends the flag; turn this on to show the banner again.
 */
const SHOW_WORKFLOW_MISSING_BANNER = false;

/** 5A / R13: refetch every 3 s while anything is SENDING, for at most 60 s. */
const SPEND_POLL_INTERVAL_MS = 3_000;
const SPEND_POLL_MAX_MS = 60_000;

/** `spendBusy` marker for the UJP's own request (amendments use their id). */
const SPEND_UJP = 'ujp';

/**
 * One key per SENDING request (`<slot>@<claimedAt>`), '' when every request is
 * final. The poll restarts only when this changes — a refetch that still reads
 * SENDING keeps the same key, so the 60 s budget holds; a new claim (e.g. a
 * biaya tambahan approved meanwhile) gets its own window.
 */
const spendSendingKey = (d: UjpDetail | null): string => {
  if (!d) return '';
  const keys: string[] = [];
  const own = d.spendControl?.request;
  if (own?.state === 'SENDING') keys.push(`${SPEND_UJP}@${own.claimedAt}`);
  for (const a of d.amendments ?? []) {
    if (a.spendRequest?.state === 'SENDING') {
      keys.push(`${a.id}@${a.spendRequest.claimedAt}`);
    }
  }
  return keys.join('|');
};

/** The detail with one request replaced by what a send returned. */
const withSpendRequest = (
  d: UjpDetail,
  amendmentId: string | null,
  view: UjpSpendRequestView
): UjpDetail =>
  amendmentId
    ? {
        ...d,
        amendments: (d.amendments ?? []).map((a) =>
          a.id === amendmentId ? { ...a, spendRequest: view } : a
        ),
      }
    : {
        ...d,
        spendControl: {
          required: d.spendControl?.required ?? true,
          listUrl: d.spendControl?.listUrl ?? null,
          request: view,
        },
      };

// ─────────────────────────────────────────────────────────────────────────────
// Panel
// ─────────────────────────────────────────────────────────────────────────────

export interface UjpDetailPanelProps {
  /** The UJP to load; null closes the panel. */
  ujpId: string | null;
  onClose: () => void;
  /** A decision landed — the list should refetch its rows and counts. */
  onChanged?: () => void;
  /**
   * Requester pressed "Ubah" (SUBMITTED) or "Ubah & ajukan ulang" (REJECTED) —
   * opens the wizard in edit mode (CR-3).
   */
  onEdit?: (detail: UjpDetail) => void;
}

const UjpDetailPanel: React.FC<UjpDetailPanelProps> = ({
  ujpId,
  onClose,
  onChanged,
  onEdit,
}) => {
  const c = UJP_COPY.panel;

  const [detail, setDetail] = useState<UjpDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  /** Set by a 409 — someone else decided while this panel was open. */
  const [conflict, setConflict] = useState<string | null>(null);
  /** Set by a 409 UJP_STALE — the requester edited since the panel loaded. */
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState<'approve' | 'reject' | 'cancel' | null>(null);
  const [decision, setDecision] = useState<UjpDecisionMode | null>(null);
  /** Negative-margin approve confirm (CR4-D7). */
  const [marginConfirm, setMarginConfirm] = useState(false);
  /** CR-7: the "Ajukan biaya tambahan" form. */
  const [amendOpen, setAmendOpen] = useState(false);
  /** CR-7: the pending amendment being rejected (reason picker open). */
  const [amendReject, setAmendReject] = useState<UjpAmendment | null>(null);
  /** CR-7: which amendment decision is in flight. */
  const [amendBusy, setAmendBusy] = useState<{
    id: string;
    kind: 'approve' | 'reject';
  } | null>(null);
  /** Spend Control send in flight: `SPEND_UJP` or the amendment id. */
  const [spendBusy, setSpendBusy] = useState<string | null>(null);
  /** 6A: the UNKNOWN request being resent (amendment null = the UJP's own). */
  const [resendTarget, setResendTarget] = useState<{
    amendment: UjpAmendment | null;
  } | null>(null);

  // Latest-value refs for the silent poll (R13): a response is only applied
  // while the drawer still shows the UJP it was fetched for.
  const idRef = useRef(ujpId);
  idRef.current = ujpId;
  const detailRef = useRef(detail);
  detailRef.current = detail;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const res = await ujpAPI.get(id);
        setDetail(res);
      } catch (err) {
        const status = err instanceof UjpApiError ? err.status : undefined;
        setDetail(null);
        if (status === 404) setNotFound(true);
        else setError(err instanceof Error ? err.message : c.loadFailed);
      } finally {
        setLoading(false);
      }
    },
    [c.loadFailed]
  );

  useEffect(() => {
    if (!ujpId) {
      setDetail(null);
      setConflict(null);
      setStale(false);
      setDecision(null);
      setMarginConfirm(false);
      setBusy(null);
      setAmendOpen(false);
      setAmendReject(null);
      setAmendBusy(null);
      setSpendBusy(null);
      setResendTarget(null);
      return;
    }
    load(ujpId);
  }, [ujpId, load]);

  /**
   * R13: the silent reload behind the Spend Control poll — unlike `load` it
   * never touches `loading` / `error` (no skeleton flash, no empty drawer), a
   * failed fetch keeps the last state on screen, and a response for a UJP the
   * drawer has since left is dropped. When the last SENDING settles the list
   * refetches once, so its Spend Control chip catches up.
   */
  const refresh = useCallback(async (id: string) => {
    try {
      const res = await ujpAPI.get(id);
      if (idRef.current !== id) return;
      const settled =
        !!spendSendingKey(detailRef.current) && !spendSendingKey(res);
      setDetail(res);
      if (settled) onChangedRef.current?.();
    } catch {
      /* a poll that fails keeps what is on screen; the next tick retries */
    }
  }, []);

  // 5A: while a request is SENDING, refetch every 3 s; stop once every state
  // is final, after 60 s, when the drawer moves to another UJP, or on unmount.
  // Each tick waits for the previous fetch, so slow answers never pile up.
  const spendPollKey =
    detail && detail.id === ujpId ? spendSendingKey(detail) : '';
  useEffect(() => {
    if (!ujpId || !spendPollKey) return;
    const id = ujpId;
    const deadline = Date.now() + SPEND_POLL_MAX_MS;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (stopped || Date.now() >= deadline) return;
      await refresh(id);
      if (!stopped) timer = setTimeout(tick, SPEND_POLL_INTERVAL_MS);
    };
    timer = setTimeout(tick, SPEND_POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [ujpId, spendPollKey, refresh]);

  // ── Decisions ──────────────────────────────────────────────────────────────

  /**
   * A 409 means the row moved on. Reload the truth and tell the two cases apart
   * (CR3-D5): still SUBMITTED → the requester edited it (stale version); already
   * decided → someone else approved/rejected first.
   */
  const handleConflict = useCallback(
    async (id: string) => {
      try {
        const fresh = await ujpAPI.get(id);
        setDetail(fresh);
        if (fresh.status === 'SUBMITTED') {
          setStale(true);
          setConflict(null);
        } else {
          setStale(false);
          setConflict(fresh.decidedBy ?? '—');
        }
      } catch {
        setConflict('—');
      }
      onChanged?.();
    },
    [onChanged]
  );

  const runDecision = useCallback(
    async (
      kind: 'approve' | 'reject' | 'cancel',
      payload?: { reasonCode: string; note?: string },
      opts?: { confirmNegativeMargin?: boolean }
    ) => {
      if (!detail) return;
      setBusy(kind);
      setConflict(null);
      setStale(false);
      setMarginConfirm(false);
      try {
        if (kind === 'cancel') {
          await ujpAPI.cancel(detail.id);
          toast.success(UJP_COPY.toast.cancelled);
        } else if (kind === 'reject') {
          await ujpAPI.decide(detail.id, {
            action: 'rejected',
            reasonCode: payload?.reasonCode,
            note: payload?.note,
            expectedVersion: detail.version,
          });
          toast.success(UJP_COPY.toast.rejected(detail.referenceId));
        } else {
          const res = await ujpAPI.decide(detail.id, {
            action: 'approved',
            expectedVersion: detail.version,
            ...(opts?.confirmNegativeMargin
              ? { confirmNegativeMargin: true }
              : {}),
          });
          if (res.shipment?.waybill) {
            toast.success(
              UJP_COPY.toast.approved(detail.referenceId, res.shipment.waybill)
            );
          } else {
            toast.success(UJP_COPY.toast.approvedSubcon(detail.referenceId));
          }
          // CR5-D3: a plan pin that no longer fit was swapped for the slot
          // default — say so per stop (seq is 1-based among the SHIPPED stops).
          if (res.workflowFallback?.length) {
            toast.info(
              UJP_COPY.toast.workflowFallbackTitle(res.workflowFallback.length),
              {
                description: res.workflowFallback
                  .map((f) =>
                    UJP_COPY.toast.workflowFallback(
                      f.seq,
                      workflowPinProblemLabel(f.reason)
                    )
                  )
                  .join(' · '),
              }
            );
          }
        }
        setDecision(null);
        await load(detail.id);
        onChanged?.();
      } catch (err) {
        const status = err instanceof UjpApiError ? err.status : undefined;
        const code = err instanceof UjpApiError ? err.code : undefined;
        if (status === 409 && code === 'NEGATIVE_MARGIN_CONFIRM_REQUIRED') {
          // The server computed a negative margin and wants an explicit
          // approver confirm (CR4-D7). Revenue is only known at approve time,
          // so this is the first point the margin sign is available — open the
          // confirm and let the approver re-run with confirmNegativeMargin.
          setMarginConfirm(true);
        } else if (status === 409) {
          setDecision(null);
          await handleConflict(detail.id);
        } else {
          const label =
            kind === 'cancel'
              ? UJP_COPY.toast.cancelFailed
              : UJP_COPY.toast.decisionFailed;
          toast.error(label, {
            description: err instanceof Error ? err.message : undefined,
          });
        }
      } finally {
        setBusy(null);
      }
    },
    [detail, load, onChanged, handleConflict]
  );

  // ── Biaya tambahan (CR-7) ──────────────────────────────────────────────────

  const handleAmendmentCreated = useCallback(
    async (seq: number) => {
      if (!detail) return;
      setAmendOpen(false);
      toast.success(UJP_COPY.toast.amendmentSubmitted(detail.referenceId, seq));
      await load(detail.id);
      onChanged?.();
    },
    [detail, load, onChanged]
  );

  /**
   * Approve is a direct click (like the UJP approve); reject comes through the
   * shared reason modal. A 409 `AMENDMENT_DECIDED` means another approver got
   * there first: say so and reload the truth.
   */
  const runAmendmentDecision = useCallback(
    async (
      amendment: UjpAmendment,
      kind: 'approve' | 'reject',
      payload?: { reasonCode: string; note?: string }
    ) => {
      if (!detail) return;
      setAmendBusy({ id: amendment.id, kind });
      try {
        await ujpAPI.decideAmendment(detail.id, amendment.id, {
          action: kind === 'approve' ? 'approved' : 'rejected',
          ...(kind === 'reject'
            ? { reasonCode: payload?.reasonCode, note: payload?.note }
            : {}),
        });
        toast.success(
          kind === 'approve'
            ? UJP_COPY.toast.amendmentApproved(detail.referenceId, amendment.seq)
            : UJP_COPY.toast.amendmentRejected(detail.referenceId, amendment.seq)
        );
        setAmendReject(null);
        await load(detail.id);
        onChanged?.();
      } catch (err) {
        const code = ujpAmendmentErrorCode(err);
        toast.error(UJP_COPY.toast.amendmentDecisionFailed, {
          description: code
            ? UJP_COPY.amendment.errors[code]
            : err instanceof Error
              ? err.message
              : undefined,
        });
        if (code === 'AMENDMENT_DECIDED') {
          setAmendReject(null);
          await load(detail.id);
          onChanged?.();
        }
      } finally {
        setAmendBusy(null);
      }
    },
    [detail, load, onChanged]
  );

  const copyAccount = () => {
    const number = detail?.payee.accountNumber ?? '';
    try {
      navigator.clipboard?.writeText(number.replace(/\D/g, ''));
    } catch {
      /* clipboard unavailable — the number is still on screen */
    }
    toast.success(UJP_COPY.toast.copied);
  };

  // ── Spend Control (DT3) ────────────────────────────────────────────────────

  /**
   * Send / resend one request: the UJP's own (`amendment` null) or one
   * APPROVED biaya tambahan's. The POST waits for Basecamp, so its answer is
   * final: show it and toast it, then reload silently for the server's truth
   * (also after an error — a 409 means the state moved on) and let the list
   * refetch its chip. `force` only ever comes from the 6A confirmation.
   */
  const sendSpendRequest = useCallback(
    async (amendment: UjpAmendment | null, force = false) => {
      if (!detail) return;
      const s = UJP_COPY.spend;
      const id = detail.id;
      const payload = force ? { force: true } : {};
      setSpendBusy(amendment?.id ?? SPEND_UJP);
      try {
        const res = amendment
          ? await ujpAPI.sendAmendmentSpendRequest(id, amendment.id, payload)
          : await ujpAPI.sendSpendRequest(id, payload);
        const view = res.spendRequest;
        if (idRef.current === id) {
          setDetail((prev) =>
            prev && prev.id === id
              ? withSpendRequest(prev, amendment?.id ?? null, view)
              : prev
          );
        }
        if (view.state === 'SENT') {
          toast.success(s.toastSent, {
            description: view.requestCode ?? undefined,
          });
        } else if (view.state === 'FAILED') {
          toast.error(s.toastFailed, { description: view.error ?? undefined });
        } else if (view.state === 'UNKNOWN') {
          toast.warning(s.toastUnknown, { description: s.unknown });
        }
        // SENDING (not expected — the call waits) is picked up by the poll.
      } catch (err) {
        toast.error(s.toastSendFailed, {
          description: err instanceof Error ? err.message : undefined,
        });
      } finally {
        setSpendBusy(null);
        setResendTarget(null);
      }
      await refresh(id);
      onChanged?.();
    },
    [detail, refresh, onChanged]
  );

  // ── Derived ────────────────────────────────────────────────────────────────

  const isSubcon = detail?.payee.type === 'subcon';
  const masked = !!detail?.masked;
  // CR8-D1: the client rule is the DEFAULT a pool stop's `charged` falls back to.
  const clientRule = !!detail?.clientConfig?.chargedPositioning;
  const revenue = detail?.revenue ?? null;
  const negativeMargin =
    revenue?.status === 'OK' && revenue.margin != null && toNum(revenue.margin) < 0;
  const stops: UjpRouteStop[] = detail
    ? [...detail.route.stops].sort((a, b) => a.seq - b.seq)
    : [];
  // CR8-D5: EVERY stop reaches the shipment, pool legs included; billing only
  // decides which legs count towards km. The server froze both flags on
  // `costs`; a pre-CR-8 row falls back to the estimate echo / the local rule.
  const shipStops = stops;
  const chargedApplied =
    detail?.costs.chargedPositioningApplied ??
    detail?.estimate?.chargedPositioning ??
    clientRule;
  const chargedOverride =
    detail?.costs.chargedPositioningOverride ??
    stops.some((s) => isChargedOverride(s, clientRule));

  const viewer = detail?.viewer;
  const amendments: UjpAmendment[] = detail?.amendments ?? [];
  const canAmend = !!viewer?.canAmend && detail?.status === 'APPROVED';
  const canDecideAmendment = !!viewer?.canDecideAmendment;
  // The section only earns its place once a UJP can carry amendments.
  const showAmendments = detail?.status === 'APPROVED' || amendments.length > 0;
  const canDecide = !!viewer?.canDecide && detail?.status === 'SUBMITTED';
  const canCancel = !!viewer?.canCancel && detail?.status === 'SUBMITTED';
  const isRequester = !!viewer?.isRequester;
  // The requester may edit while waiting, or fix + resubmit after a reject (CR-3).
  const canEditSubmitted = isRequester && detail?.status === 'SUBMITTED';
  const canEditRejected = isRequester && detail?.status === 'REJECTED';
  // Spend Control (1A / 11A): everyone sees the status of an APPROVED UJP;
  // only `viewer.canSendSpendRequest` (approver / manager) gets the buttons.
  // A server without Spend Control sends no `spendControl` → nothing shown.
  const spendControl = detail?.spendControl ?? null;
  const showSpendControl = detail?.status === 'APPROVED' && !!spendControl;
  const canSendSpend =
    !!viewer?.canSendSpendRequest && detail?.status === 'APPROVED';
  const transferAmount = masked ? null : (detail?.totals?.transfer ?? null);

  const title = detail ? (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="font-mono text-[13px] font-bold tracking-tight text-text-strong-950">
        {detail.referenceId}
      </span>
      <UjpStatusBadge status={detail.status} />
      {detail.version != null && detail.version > 1 && (
        <Badge status="neutral" size="sm">
          {c.version(detail.version)}
        </Badge>
      )}
      {detail.header.isReverse && (
        <Badge status="feature" size="sm">
          {UJP_COPY.page.reverse}
        </Badge>
      )}
      {isSubcon && (
        <Badge status="neutral" size="sm">
          {c.subcon}
        </Badge>
      )}
    </span>
  ) : (
    UJP_COPY.page.title
  );

  const subtitle = detail ? (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-text-sub-600">
        <span>{detail.client?.name}</span>
        <span aria-hidden>·</span>
        <span>{formatDate(detail.header.deliveryDate)}</span>
        <span aria-hidden>·</span>
        <span>{c.requester(detail.requesterEmail)}</span>
        <UjpAgeBadge createdAt={detail.createdAt} />
      </span>
      {detail.status === 'SUBMITTED' && viewer?.isRequester && (
        <span className="text-[12px] text-text-soft-400">{c.waitingFinance}</span>
      )}
    </span>
  ) : undefined;

  const footer = detail ? (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        {canEditSubmitted && (
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => detail && onEdit?.(detail)}
            disabled={!!busy}
          >
            {c.edit}
          </Button>
        )}
        {canCancel && (
          <Button
            tone="neutral"
            style="ghost"
            onClick={() => setDecision('cancel')}
            disabled={!!busy}
          >
            {c.cancel}
          </Button>
        )}
        {canEditRejected && (
          <Button onClick={() => detail && onEdit?.(detail)}>
            {c.editResubmit}
          </Button>
        )}
        {/* Why there is no Setujui/Tolak: an approver looking at their own
            request (self-approve is blocked server-side), or a plain viewer.
            A requester who is not an approver already has Ubah/Batalkan. */}
        {!canDecide &&
          detail.status === 'SUBMITTED' &&
          (viewer?.isApprover || !viewer?.isRequester) && (
            <span className="text-[12px] text-text-soft-400">
              {viewer?.isApprover ? c.ownRequestHint : c.notApproverHint}
            </span>
          )}
      </div>
      {canDecide && (
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="destructive"
            style="stroke"
            onClick={() => setDecision('reject')}
            disabled={!!busy}
          >
            {c.reject}
          </Button>
          <Button
            onClick={() =>
              negativeMargin ? setMarginConfirm(true) : runDecision('approve')
            }
            loading={busy === 'approve'}
            disabled={!!busy}
          >
            {busy === 'approve' ? c.approving : c.approve}
          </Button>
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <>
      <FormModal
        open={!!ujpId}
        onClose={busy ? () => undefined : onClose}
        position="right"
        size="md"
        padded={false}
        title={title}
        subtitle={subtitle}
        footer={footer}
        closeOnBackdropClick={!busy}
        closeOnEscape={!busy}
      >
        {loading && !detail ? (
          <div className="space-y-3 px-5 py-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : notFound ? (
          <EmptyState size="sm">
            <EmptyStateTitle>{c.notFound}</EmptyStateTitle>
          </EmptyState>
        ) : error ? (
          <EmptyState size="sm">
            <EmptyStateTitle>{c.loadFailed}</EmptyStateTitle>
            <EmptyStateDescription>{error}</EmptyStateDescription>
          </EmptyState>
        ) : detail ? (
          <div>
            {conflict && (
              <div className="px-5 pt-4">
                <Banner status="warning" title={c.alreadyDecided(conflict)}>
                  {c.conflictReloaded}
                </Banner>
              </div>
            )}

            {stale && (
              <div className="px-5 pt-4">
                <Banner status="warning" title={c.updatedTitle}>
                  {c.updatedBody}
                </Banner>
              </div>
            )}

            {detail.status === 'REJECTED' && detail.rejection && (
              <div className="px-5 pt-4">
                <Banner
                  status="error"
                  title={c.rejectedHeading(
                    detail.rejection.reasonLabel ?? detail.rejection.reasonCode
                  )}
                >
                  {detail.rejection.note && <p>{detail.rejection.note}</p>}
                  {detail.decidedBy && (
                    <p className="text-[11px] text-text-soft-400">
                      {c.decidedByAt(
                        detail.decidedBy,
                        formatDateTime(detail.decidedAt)
                      )}
                    </p>
                  )}
                </Banner>
              </div>
            )}

            {/* 1 — money first */}
            <Section>
              {masked || !detail.totals ? (
                <p className="text-[13px] text-text-sub-600">
                  {c.maskedTitle}
                </p>
              ) : (
                <UjpTiles totals={detail.totals} eMoney={detail.vehicle.eMoney} />
              )}
              <div className="mt-3 flex items-center justify-between border-t border-stroke-soft-200 pt-3">
                <span className="label-eyebrow">{c.totalLabel}</span>
                {masked || !detail.totals ? (
                  <UjpHiddenAmount />
                ) : (
                  <span
                    className="font-mono text-[20px] font-extrabold tabular-nums text-text-strong-950"
                    aria-live="polite"
                  >
                    {formatRupiah(detail.totals.total)}
                  </span>
                )}
              </div>
            </Section>

            {/* 1b — revenue & margin (CR-4): approver-only, locked for others */}
            {viewer?.isApprover ? (
              <Section title={c.revenueHeading}>
                <RevenueBlock revenue={revenue} status={detail.status} />
              </Section>
            ) : viewer?.isRequester ? (
              <Section title={c.revenueHeading}>
                <p className="inline-flex items-center gap-1.5 text-[12px] text-text-sub-600">
                  <RiLockLine className="size-3.5" aria-hidden />
                  {c.priceLocked}
                </p>
              </Section>
            ) : null}

            {/* 2 — what approving creates */}
            <Section
              title={
                detail.status === 'APPROVED' && detail.shipment
                  ? c.shipmentHeading
                  : c.createdOnApprove
              }
            >
              <ShipmentPreview detail={detail} shipStops={shipStops} />
            </Section>

            {/* 2b — the transfer request in Spend Control (1A, APPROVED only) */}
            {showSpendControl && spendControl && (
              <Section title={UJP_COPY.spend.heading}>
                <SpendRequestRows
                  testId="ujp-spend-control"
                  request={spendControl.request}
                  required={spendControl.required}
                  canSend={canSendSpend}
                  busy={spendBusy === SPEND_UJP}
                  disabled={!!spendBusy || !!busy}
                  onSend={() => sendSpendRequest(null)}
                  onResend={() => setResendTarget({ amendment: null })}
                >
                  {transferAmount != null && (
                    <KV k={UJP_COPY.spend.transferLine}>
                      <Mono>{formatRupiah(transferAmount)}</Mono>
                    </KV>
                  )}
                </SpendRequestRows>
              </Section>
            )}

            {/* 3 — the numbers behind the total */}
            <Section title={c.breakdown}>
              <div>
                {isSubcon ? (
                  <KV k={c.payeeSubcon} sub={c.payeeSubconHint}>
                    {masked || detail.payee.nominalTransfer == null ? (
                      <UjpHiddenAmount />
                    ) : (
                      <Mono>{formatRupiah(detail.payee.nominalTransfer)}</Mono>
                    )}
                  </KV>
                ) : (
                  <CostBreakdown
                    detail={detail}
                    chargedApplied={chargedApplied}
                    chargedOverride={chargedOverride}
                  />
                )}
                <KV k={c.eMoney}>
                  {UJP_COPY.cost.eMoneyOptions[detail.vehicle.eMoney] ?? '—'}
                </KV>
                {detail.vehicle.eMoneyCardNo && (
                  <KV k={c.eMoneyCardNo}>
                    <span className="font-mono">{detail.vehicle.eMoneyCardNo}</span>
                  </KV>
                )}
              </div>
            </Section>

            {/* 3b — biaya tambahan (CR-7): post-approval extra costs */}
            {showAmendments && (
              <Section title={UJP_COPY.amendment.section}>
                <AmendmentsBlock
                  detail={detail}
                  amendments={amendments}
                  masked={masked}
                  canAmend={canAmend}
                  canDecideAmendment={canDecideAmendment}
                  busy={amendBusy}
                  disabled={!!busy}
                  onCreate={() => setAmendOpen(true)}
                  onApprove={(a) => runAmendmentDecision(a, 'approve')}
                  onReject={(a) => setAmendReject(a)}
                  spend={
                    showSpendControl
                      ? {
                          canSend: canSendSpend,
                          busyId: spendBusy,
                          onSend: (a) => sendSpendRequest(a),
                          onResend: (a) => setResendTarget({ amendment: a }),
                        }
                      : null
                  }
                />
              </Section>
            )}

            {/* 4 — where the money lands */}
            <Section title={isSubcon ? c.bankSubcon : c.bankDriver}>
              <div>
                <KV k={isSubcon ? c.vendor : c.driver}>
                  {(isSubcon ? detail.vendor?.name : detail.rider?.name) ?? '—'}
                </KV>
                <KV k={UJP_COPY.driver.bankName}>{detail.payee.bankName || '—'}</KV>
                <KV k={UJP_COPY.driver.accountNumber}>
                  {masked ? (
                    <span
                      className="inline-flex items-center gap-1 font-mono tabular-nums text-text-sub-600"
                      title={c.maskedTitle}
                    >
                      <RiLockLine className="size-3.5" aria-hidden />
                      {formatMaskedAccount(detail.payee.accountNumber ?? '')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <Mono>{detail.payee.accountNumber || '—'}</Mono>
                      <Button
                        tone="neutral"
                        style="ghost"
                        size="xs"
                        onClick={copyAccount}
                      >
                        <RiFileCopyLine />
                        {c.copy}
                      </Button>
                    </span>
                  )}
                </KV>
                <KV k={UJP_COPY.driver.accountHolder}>
                  {detail.payee.accountHolder || '—'}
                </KV>
              </div>
            </Section>

            {/* 5 — the route, with what will and will not be charged (CR-8:
                per stop as the server stored it, its own override else the
                client rule; the hint sits on the first / last stop, the pool
                by position since CR-8b) */}
            <Section title={c.routeHeading(stops.length)}>
              <ol className="space-y-2">
                {stops.map((stop, i) => {
                  const pool = isPool(stop);
                  const isEdge = i === 0 || i === stops.length - 1;
                  const billed = isStopCharged(stop, clientRule);
                  const overrides = isChargedOverride(stop, clientRule);
                  const legKm = detail.route.legs.find((l) => l.toSeq === stop.seq);
                  return (
                    <li
                      key={`${stop.seq}-${stop.name}`}
                      className="flex items-start gap-2.5"
                    >
                      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-gsm-sm bg-bg-weak-50 font-mono text-[10px] font-bold tabular-nums text-text-sub-600">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold tracking-tight text-text-strong-950">
                          {stop.name}
                          <Badge status={pool ? 'neutral' : 'feature'} size="sm">
                            {stopKindLabel(stop)}
                          </Badge>
                          {isEdge && (
                            <span
                              className="text-[11px] font-normal text-text-soft-400"
                              data-testid={`ujp-stop-${stop.seq}-charged`}
                            >
                              {billed ? c.charged : c.notCharged}
                            </span>
                          )}
                          {overrides && (
                            <Tag
                              variant="stroke"
                              size="xs"
                              className="font-normal text-warning-dark"
                              data-testid={`ujp-stop-${stop.seq}-charged-override`}
                            >
                              {UJP_COPY.route.chargedOverride}
                            </Tag>
                          )}
                        </p>
                        <p className="text-[11px] text-text-sub-600">{stop.address}</p>
                        {stop.workflowName && (
                          <p className="text-[11px] text-text-soft-400">
                            {UJP_COPY.route.workflow} · {stop.workflowName}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 font-mono text-[11px] tabular-nums text-text-soft-400">
                        {i > 0 && legKm ? formatKm(legKm.km) : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </Section>

            {/* 6 — who did what */}
            <Section title={c.history}>
              <ol className="space-y-2">
                {detail.history.map((h, i) => (
                  <HistoryRow key={`${h.at}-${i}`} entry={h} />
                ))}
                {detail.history.length === 0 && (
                  <li className="text-[12px] text-text-soft-400">—</li>
                )}
              </ol>
            </Section>
          </div>
        ) : null}
      </FormModal>

      <UjpDecisionModal
        mode={decision}
        open={!!decision}
        referenceId={detail?.referenceId ?? ''}
        submitting={busy === 'reject' || busy === 'cancel'}
        onClose={() => setDecision(null)}
        onConfirm={(payload) =>
          runDecision(decision === 'cancel' ? 'cancel' : 'reject', payload)
        }
      />

      <UjpAmendmentModal
        open={amendOpen}
        ujpId={detail?.id ?? ''}
        referenceId={detail?.referenceId ?? ''}
        onClose={() => setAmendOpen(false)}
        onCreated={(res) => handleAmendmentCreated(res.seq)}
      />

      <ResendSpendRequestModal
        open={!!resendTarget}
        referenceId={detail?.referenceId ?? ''}
        amendmentSeq={resendTarget?.amendment?.seq ?? null}
        listUrl={spendControl?.listUrl ?? null}
        sending={
          !!resendTarget &&
          spendBusy === (resendTarget.amendment?.id ?? SPEND_UJP)
        }
        onClose={() => setResendTarget(null)}
        onConfirm={() =>
          resendTarget && sendSpendRequest(resendTarget.amendment, true)
        }
      />

      <UjpDecisionModal
        mode={amendReject ? 'reject' : null}
        open={!!amendReject}
        referenceId={
          amendReject ? UJP_COPY.amendment.seq(amendReject.seq) : ''
        }
        labels={{
          title: UJP_COPY.amendment.rejectTitle,
          subtitle: UJP_COPY.amendment.rejectSubtitle,
          cta: UJP_COPY.amendment.rejectTitle,
        }}
        submitting={amendBusy?.kind === 'reject'}
        onClose={() => setAmendReject(null)}
        onConfirm={(payload) =>
          amendReject && runAmendmentDecision(amendReject, 'reject', payload)
        }
      />

      <FormModal
        open={marginConfirm}
        onClose={busy ? () => undefined : () => setMarginConfirm(false)}
        icon={<RiAlertLine />}
        iconTone="danger"
        title={c.marginConfirmTitle(detail?.referenceId ?? '')}
        subtitle={c.marginNegative}
        size="sm"
        closeOnBackdropClick={!busy}
        closeOnEscape={!busy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setMarginConfirm(false)}
              disabled={!!busy}
            >
              {UJP_COPY.wizard.cancel}
            </Button>
            <Button
              tone="destructive"
              onClick={() =>
                runDecision('approve', undefined, { confirmNegativeMargin: true })
              }
              loading={busy === 'approve'}
              disabled={!!busy}
            >
              {c.marginConfirmCta}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">
          {c.marginConfirmBody(formatRupiah(revenue?.margin))}
        </p>
      </FormModal>
    </>
  );
};

/** Approver-only revenue / margin block (CR-4). */
const RevenueBlock: React.FC<{
  revenue: UjpDetail['revenue'];
  status: UjpDetail['status'];
}> = ({ revenue, status }) => {
  const c = UJP_COPY.panel;
  // Revenue is snapshotted at approve; for a SUBMITTED UJP it is not there yet.
  if (!revenue) {
    return (
      <p className="text-[12px] text-text-sub-600">
        {status === 'SUBMITTED'
          ? 'Revenue & margin dihitung saat disetujui.'
          : c.revenueUnavailable}
      </p>
    );
  }
  if (revenue.status !== 'OK') {
    return (
      <div className="flex flex-col gap-1">
        <Badge status="warning" size="sm">
          {c.revenueUnavailable}
        </Badge>
        <span className="text-[11px] text-text-soft-400">
          {c.revenueStatusHint[revenue.status] ?? ''}
        </span>
      </div>
    );
  }
  const marginNum = toNum(revenue.margin);
  const snap = revenue.tariff ?? null;
  const notes = parseRevenueNotes(revenue.note);
  // Komisi: prefer the flat field, fall back to the snapshot's applied share.
  const komisi = revenue.komisi ?? snap?.komisi?.applied ?? null;
  const surcharge = snap?.surcharge;
  const rateSource = snap?.rateSource;
  const rateSourceLabel =
    rateSource && rateSource in c.rateSource
      ? c.rateSource[rateSource as keyof typeof c.rateSource]
      : null;
  return (
    <div>
      {/* Compact breakdown from the frozen snapshot (CR-4b §20.3) — each line
          only when the snapshot carries it, so old snapshots still render. */}
      {snap?.rateApplied != null && (
        <KV
          k={c.rateApplied}
          sub={[
            rateSourceLabel,
            snap.bodyKey ? c.rateBody(snap.bodyKey) : null,
            snap.tier ? c.tierHit(formatKm(snap.tier.maxKm)) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          <Mono>{formatRupiah(snap.rateApplied)}</Mono>
        </KV>
      )}
      {snap && toNum(snap.asuransi) > 0 && (
        <KV k={c.asuransiLine}>
          <Mono>{formatRupiah(snap.asuransi)}</Mono>
        </KV>
      )}
      {surcharge && toNum(surcharge.amount) > 0 && (
        <KV
          k={c.surchargeLine}
          sub={c.surchargeSub(
            surcharge.titik,
            surcharge.freePoints,
            formatRupiah(surcharge.extraRate)
          )}
        >
          <Mono>{formatRupiah(surcharge.amount)}</Mono>
        </KV>
      )}
      {snap?.reverseAmount != null && toNum(snap.reverseAmount) > 0 && (
        <KV k={c.reverseRevenueLine}>
          <Mono>{formatRupiah(snap.reverseAmount)}</Mono>
        </KV>
      )}
      {komisi != null && toNum(komisi) > 0 && (
        <KV k={c.komisiLine} sub={c.komisiSub}>
          <Mono>− {formatRupiah(komisi)}</Mono>
        </KV>
      )}
      <KV k={c.revenueLabel}>
        <Mono>{formatRupiah(revenue.amount)}</Mono>
      </KV>
      <KV k={c.marginLabel}>
        <span
          className={`font-mono tabular-nums ${marginNum < 0 ? 'text-error-base' : 'text-success-base'}`}
        >
          {formatRupiah(revenue.margin)}
        </span>
      </KV>
      {notes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {notes.map((code) => (
            <li key={code} className="text-[11px] text-warning-dark">
              {c.revenueNotes[code]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-blocks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * "Dibuat saat disetujui" — the one line that says what approve will do.
 * Since CR8-D5 `shipStops` is every stop on the route, pool legs included.
 */
const ShipmentPreview: React.FC<{
  detail: UjpDetail;
  shipStops: UjpRouteStop[];
}> = ({ detail, shipStops }) => {
  const c = UJP_COPY.panel;

  // Subcon now creates a shipment on the same path as a driver payee (it rides
  // on the shared driver ops picks), so it surfaces its shipment here too — no
  // subcon short-circuit any more.
  if (detail.status === 'APPROVED' && detail.shipment) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-text-sub-600">
        <Badge status="success" size="sm">
          {c.created}
        </Badge>
        <span>
          {c.direct4w} · {c.stopsShipped(shipStops.length)} ·{' '}
          <Link
            to={`/shipments?detail=${detail.shipment.id}`}
            className="font-mono font-semibold text-accent underline-offset-2 hover:underline"
          >
            {c.viewShipment(detail.shipment.waybill)}
          </Link>
        </span>
      </div>
    );
  }

  if (detail.status !== 'SUBMITTED') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-text-sub-600">
        <Badge status="neutral" size="sm">
          {c.notCreated}
        </Badge>
        <span>
          {c.notCreatedFinal(
            UJP_COPY.status[detail.status].toLowerCase()
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-start gap-2 text-[13px] text-text-sub-600">
        <Badge status="information" size="sm">
          {c.notCreated}
        </Badge>
        <span>
          {c.direct4w} · {c.stopsShipped(shipStops.length)} ·{' '}
          {shipStops[0]?.name ?? '—'}{' '}
          <span className="text-text-soft-400">→</span>{' '}
          {shipStops[shipStops.length - 1]?.name ?? '—'} ·{' '}
          {UJP_COPY.panel.driver} <b>{detail.rider?.name ?? '—'}</b> ·{' '}
          <span className="font-mono">{detail.vehicle.plateNumber}</span>
        </span>
      </div>
      <p className="text-[11px] text-text-soft-400">
        {c.approveCreates} {c.allStopsShipped}
      </p>
    </div>
  );
};

/** A request's state, plus the two "nothing sent" cases (3A). */
type SpendDisplayState = UjpSpendRequestState | 'NOT_SENT' | 'NOT_REQUIRED';

/** Every badge carries its text; only SENDING moves (10A). */
const SpendStateBadge: React.FC<{ state: SpendDisplayState }> = ({ state }) => {
  const label = UJP_COPY.spend.badge[state];
  switch (state) {
    case 'SENDING':
      return (
        <StatusBadge status="information" pulse>
          {label}
        </StatusBadge>
      );
    case 'SENT':
      return (
        <Badge status="success" size="sm">
          {label}
        </Badge>
      );
    case 'FAILED':
      return (
        <Badge status="error" size="sm">
          {label}
        </Badge>
      );
    case 'UNKNOWN':
      return (
        <Badge status="warning" size="sm">
          {label}
        </Badge>
      );
    default:
      return (
        <Badge status="neutral" size="sm">
          {label}
        </Badge>
      );
  }
};

/**
 * One Spend Control request — section 2b (DT1) or, `compact`, the line inside
 * an APPROVED biaya tambahan (DT2). Two fixed rows at every width (9A): row 1
 * = badge + the full request code (plain mono data, wraps, never truncated);
 * row 2 = time · "Buka di Spend Control" on the left, the action on the right.
 * The compact line only gets row 2 when there is a link or an action. Then
 * the section's `children` (the transfer amount) and the state's full text —
 * a FAILED error is shown whole and wraps, no tooltip.
 *
 * Actions (11A): NOT_SENT → "Kirim ke Spend Control", FAILED → "Kirim ulang"
 * (safe to repeat), UNKNOWN → "Kirim ulang…" via the 6A confirmation. Without
 * `canSend`, FAILED / UNKNOWN show "Hubungi approver UJP untuk kirim ulang."
 * in the button's place. The wrapper is `aria-live="polite"` (10A), so the
 * poll's SENDING → Terkirim / Gagal / Belum pasti is read out once.
 */
const SpendRequestRows: React.FC<{
  request: UjpSpendRequestView | null;
  /** false = no transfer to send ("Tidak perlu"); only read when `request` is null. */
  required: boolean;
  canSend: boolean;
  /** This request's send is in flight. */
  busy: boolean;
  /** Another send or a decision is in flight. */
  disabled: boolean;
  compact?: boolean;
  testId: string;
  onSend: () => void;
  onResend: () => void;
  children?: React.ReactNode;
}> = ({
  request,
  required,
  canSend,
  busy,
  disabled,
  compact = false,
  testId,
  onSend,
  onResend,
  children,
}) => {
  const s = UJP_COPY.spend;
  const state: SpendDisplayState = request
    ? request.state
    : required
      ? 'NOT_SENT'
      : 'NOT_REQUIRED';

  const action =
    state === 'NOT_SENT'
      ? 'send'
      : state === 'FAILED'
        ? 'resend'
        : state === 'UNKNOWN'
          ? 'resendConfirm'
          : null;
  const showAction = canSend && action != null;
  const askApprover = !canSend && (state === 'FAILED' || state === 'UNKNOWN');
  const time = request ? (request.sentAt ?? request.claimedAt) : null;
  const link = request?.url ?? null;
  const hasRow2 = compact
    ? !!link || showAction || askApprover
    : !!time || !!link || showAction || askApprover;

  // The section explains every state; the compact line only what needs reading.
  const message =
    state === 'FAILED'
      ? request?.error || null
      : state === 'UNKNOWN'
        ? s.unknown
        : compact
          ? null
          : state === 'NOT_SENT'
            ? s.notSent
            : state === 'NOT_REQUIRED'
              ? s.notRequired
              : state === 'SENDING'
                ? s.sending
                : null;
  const workflowMissing =
    SHOW_WORKFLOW_MISSING_BANNER &&
    state === 'SENT' &&
    request?.workflowConfigured === false;

  const label =
    action === 'send' ? s.send : action === 'resend' ? s.resend : s.resendConfirm;

  return (
    <div
      aria-live="polite"
      data-testid={testId}
      data-state={state}
      className={
        compact
          ? 'mt-2 space-y-1.5 border-t border-stroke-soft-200 pt-2'
          : 'space-y-2'
      }
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {compact && (
          <span className="text-[11px] font-semibold text-text-sub-600">
            {s.lineLabel}
          </span>
        )}
        <SpendStateBadge state={state} />
        {request?.requestCode && (
          <span
            className="min-w-0 break-all font-mono text-[12px] tabular-nums text-text-strong-950"
            data-testid={`${testId}-code`}
          >
            {request.requestCode}
          </span>
        )}
      </div>

      {hasRow2 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[12px] text-text-sub-600">
            {time && (
              <span className="font-mono tabular-nums">{formatDateTime(time)}</span>
            )}
            {time && link && <span aria-hidden>·</span>}
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-semibold text-accent underline-offset-2 hover:underline"
              >
                {s.open}
                <RiExternalLinkLine className="size-3.5" aria-hidden />
              </a>
            )}
          </span>
          {showAction ? (
            <Button
              tone="neutral"
              style="stroke"
              size={compact ? 'xs' : 'sm'}
              // 12A: the section's button grows to a 44 px tap target on touch.
              className={compact ? undefined : 'pointer-coarse:min-h-11'}
              onClick={action === 'resendConfirm' ? onResend : onSend}
              loading={busy}
              disabled={disabled || busy}
            >
              {busy ? s.busy : label}
            </Button>
          ) : askApprover ? (
            <span className="text-[11px] text-text-soft-400">{s.askApprover}</span>
          ) : null}
        </div>
      )}

      {children}

      {message && (
        <p
          className="break-words text-[12px] leading-relaxed text-text-sub-600"
          data-testid={`${testId}-message`}
        >
          {message}
        </p>
      )}
      {workflowMissing && (
        <Banner status="warning" size="sm">
          {s.workflowMissing}
        </Banner>
      )}
    </div>
  );
};

/**
 * The driver-payee cost rows: km, energy and the buckets each line lands in.
 * `chargedApplied` / `chargedOverride` are the CR-8 flags the server froze on
 * `costs` (with the panel's fallbacks for a pre-CR-8 row).
 */
const CostBreakdown: React.FC<{
  detail: UjpDetail;
  chargedApplied: boolean;
  chargedOverride: boolean;
}> = ({ detail, chargedApplied, chargedOverride }) => {
  const c = UJP_COPY.panel;
  const est = detail.estimate;
  const ev = detail.vehicle.energyType === 'EV';
  const qris = usesQris(detail.vehicle.eMoney);
  const flazz =
    detail.vehicle.eMoney === 'FLAZZ' || detail.vehicle.eMoney === 'FLAZZ_QRIS';
  const bbmBucket = qris
    ? UJP_COPY.estimate.qrisShort
    : flazz
      ? UJP_COPY.estimate.flazz
      : UJP_COPY.estimate.transfer;
  const reverse = toNum(est?.reverseChargeApplied);

  return (
    <>
      <KV
        k={c.kmLine}
        sub={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {chargedApplied ? c.chargedRuleYes : c.chargedRuleNo}
            {chargedOverride && (
              <Tag
                variant="stroke"
                size="xs"
                className="text-warning-dark"
                data-testid="ujp-charged-override"
              >
                {UJP_COPY.route.chargedOverride}
              </Tag>
            )}
          </span>
        }
      >
        <Mono>
          {`${formatKm(est?.kmAllLegs, false)} / ${formatKm(est?.kmCharged, false)} / ${formatKm(detail.costs.kmYangDiajukan, false)} km`}
        </Mono>
      </KV>
      <KV k={c.kmMargin(toNum(est?.kmMarginPct))}>
        <Mono>{formatKm(est?.totalKmWithMargin)}</Mono>
      </KV>
      <KV
        k={ev ? c.energi : c.bbm}
        sub={`${formatKm(est?.estimasiBbmLiter, false)} ${ev ? 'kWh' : 'L'} × ${formatRupiah(detail.vehicle.energyPrice)}`}
      >
        <Mono>
          {formatRupiah(est?.totalBbmCost)} → {bbmBucket}
        </Mono>
      </KV>
      {/* PLAN-UJP-DYNAMIC-COSTS D6: every row with the pocket it is paid from. */}
      {(detail.costs.costItems ?? []).map((row, i) => (
        <KV key={`${i}-${row.label}`} k={row.label}>
          <span
            className="inline-flex items-center gap-2"
            data-testid="ujp-detail-cost-row"
          >
            <Mono>{formatRupiah(row.amount)}</Mono>
            <Badge status="neutral" size="sm">
              {row.payment === 'FLAZZ'
                ? UJP_COPY.estimate.flazz
                : UJP_COPY.estimate.transfer}
            </Badge>
          </span>
        </KV>
      ))}
      {detail.costs.justifikasiBiayaLainLain?.trim() && (
        <KV k={c.costNote}>
          <span className="break-words" data-testid="ujp-detail-cost-note">
            {detail.costs.justifikasiBiayaLainLain}
          </span>
        </KV>
      )}
      {reverse > 0 && (
        <KV k={c.reverseLine}>
          <Mono>
            {formatRupiah(reverse)} → {UJP_COPY.estimate.transfer}
          </Mono>
        </KV>
      )}
    </>
  );
};

/**
 * Biaya tambahan (CR-7): every amendment in submission order, the pending one
 * with Setujui / Tolak for an eligible approver, and the running total once
 * something has been approved. All gating comes from the server `viewer`.
 */
const AmendmentsBlock: React.FC<{
  detail: UjpDetail;
  amendments: UjpAmendment[];
  masked: boolean;
  canAmend: boolean;
  canDecideAmendment: boolean;
  busy: { id: string; kind: 'approve' | 'reject' } | null;
  disabled: boolean;
  onCreate: () => void;
  onApprove: (a: UjpAmendment) => void;
  onReject: (a: UjpAmendment) => void;
  /** 2A: the Spend Control line on APPROVED rows; null = no Spend Control. */
  spend: {
    canSend: boolean;
    /** `spendBusy` of the panel — an amendment id, the UJP marker, or null. */
    busyId: string | null;
    onSend: (a: UjpAmendment) => void;
    onResend: (a: UjpAmendment) => void;
  } | null;
}> = ({
  detail,
  amendments,
  masked,
  canAmend,
  canDecideAmendment,
  busy,
  disabled,
  onCreate,
  onApprove,
  onReject,
  spend,
}) => {
  const c = UJP_COPY.amendment;
  const totals = detail.totals;
  const amended = totals?.amended ?? null;
  const hasApproved = amended != null && toNum(amended) > 0;
  const showTotal =
    hasApproved || amendments.some((a) => a.status === 'APPROVED');
  const who = (email: string | null | undefined) => email?.split('@')[0] ?? '—';

  return (
    <div className="space-y-3">
      {amendments.length === 0 ? (
        <p className="text-[12px] text-text-sub-600">{c.empty}</p>
      ) : (
        <ol className="space-y-2">
          {amendments.map((a) => {
            const pending = a.status === 'SUBMITTED';
            const rowBusy = busy?.id === a.id ? busy.kind : null;
            const anyBusy = disabled || !!busy;
            return (
              <li
                key={a.id}
                className="rounded-gsm border border-stroke-soft-200 px-3 py-2.5"
                data-testid={`ujp-amendment-${a.seq}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold tracking-tight text-text-strong-950">
                      <span className="font-mono tabular-nums text-text-soft-400">
                        {c.seq(a.seq)}
                      </span>
                      <span className="break-words">{a.justifikasi}</span>
                    </p>
                    <p className="mt-0.5 text-[11px] text-text-soft-400">
                      {c.requestedBy(who(a.requestedBy), formatDateTime(a.requestedAt))}
                    </p>
                    {a.status !== 'SUBMITTED' && a.decidedBy && (
                      <p className="text-[11px] text-text-soft-400">
                        {c.decidedBy(
                          a.status === 'APPROVED' ? c.approvedVerb : c.rejectedVerb,
                          who(a.decidedBy),
                          formatDateTime(a.decidedAt)
                        )}
                        {a.reasonCode ? ` · ${c.reasonLine(a.reasonCode)}` : ''}
                      </p>
                    )}
                    {a.decisionNote && (
                      <p className="mt-0.5 text-[11px] text-text-sub-600">
                        {a.decisionNote}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {a.amount == null ? (
                      <UjpHiddenAmount />
                    ) : (
                      <Mono>
                        <span className="text-[13px] font-semibold text-text-strong-950">
                          {formatRupiah(a.amount)}
                        </span>
                      </Mono>
                    )}
                    <UjpAmendmentStatusBadge status={a.status} />
                  </div>
                </div>
                {pending && canDecideAmendment && (
                  <div className="mt-2 flex items-center justify-end gap-2 border-t border-stroke-soft-200 pt-2">
                    <Button
                      tone="destructive"
                      style="stroke"
                      size="xs"
                      onClick={() => onReject(a)}
                      disabled={anyBusy}
                    >
                      {c.reject}
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => onApprove(a)}
                      loading={rowBusy === 'approve'}
                      disabled={anyBusy}
                    >
                      {rowBusy === 'approve' ? c.approving : c.approve}
                    </Button>
                  </div>
                )}
                {spend && a.status === 'APPROVED' && (
                  <SpendRequestRows
                    compact
                    testId={`ujp-amendment-${a.seq}-spend`}
                    request={a.spendRequest ?? null}
                    // A biaya tambahan always carries an amount (> 0).
                    required
                    canSend={spend.canSend}
                    busy={spend.busyId === a.id}
                    disabled={anyBusy || !!spend.busyId}
                    onSend={() => spend.onSend(a)}
                    onResend={() => spend.onResend(a)}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}

      {showTotal && (
        <div className="border-t border-stroke-soft-200 pt-2">
          <KV k={c.amendedSum}>
            {masked || amended == null ? (
              <UjpHiddenAmount />
            ) : (
              <Mono>{formatRupiah(amended)}</Mono>
            )}
          </KV>
          <KV k={c.totalWithAmendments}>
            {masked || totals?.totalWithAmendments == null ? (
              <UjpHiddenAmount />
            ) : (
              <Mono>{formatRupiah(totals.totalWithAmendments)}</Mono>
            )}
          </KV>
        </div>
      )}

      {canAmend && (
        <div className="flex justify-end">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onCreate}
            disabled={disabled || !!busy}
          >
            <RiAddLine />
            {c.create}
          </Button>
        </div>
      )}
    </div>
  );
};

/** `amendment.<seq>.<key>` → the seq, or null for any other history field. */
const amendmentSeqOf = (changes: UjpHistoryEntry['changes']): number | null => {
  for (const ch of changes ?? []) {
    const m = /^amendment\.(\d+)\./.exec(ch.field);
    if (m) return Number(m[1]);
  }
  return null;
};

/** `amendment.2.amount` → "Biaya tambahan #2 · nominal"; other fields unchanged. */
const historyFieldLabel = (field: string): string => {
  const m = /^amendment\.(\d+)\.(\w+)$/.exec(field);
  if (!m) return UJP_COPY.panel.historyFieldName[field] ?? field;
  const key = UJP_COPY.amendment.historyField[m[2]] ?? m[2];
  return `${UJP_COPY.amendment.section} #${m[1]} · ${key}`;
};

const HistoryRow: React.FC<{ entry: UjpHistoryEntry }> = ({ entry }) => {
  const changes = entry.changes ?? [];
  const by = entry.by?.split('@')[0] ?? '—';
  // CR-7: an APPROVED → APPROVED row tagged BIAYA_TAMBAHAN_* is an amendment
  // event, not a second approval — read the verb off the note instead.
  const amendmentVerb = entry.note
    ? UJP_COPY.amendment.historyVerb[entry.note]
    : undefined;
  const verb = UJP_COPY.panel.historyVerb[entry.to] ?? entry.to;
  return (
    <li className="text-[12px]">
      <div className="flex items-start gap-2.5">
        <span className="shrink-0 font-mono tabular-nums text-text-soft-400">
          {formatDateTime(entry.at)}
        </span>
        <span className="min-w-0 text-text-sub-600">
          {amendmentVerb
            ? UJP_COPY.amendment.historyLine(
                amendmentVerb,
                amendmentSeqOf(entry.changes),
                by
              )
            : UJP_COPY.panel.historyBy(verb, by)}
          {!amendmentVerb && entry.note ? ` · ${entry.note}` : ''}
        </span>
      </div>
      {changes.length > 0 && (
        <div className="mt-1 ml-[4.5rem]">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-soft-400">
            {UJP_COPY.panel.changesTitle}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {changes.map((ch, i) => (
              <li key={`${ch.field}-${i}`} className="text-[11px] text-text-sub-600">
                {UJP_COPY.panel.changeLine(historyFieldLabel(ch.field), ch.from, ch.to)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

export default UjpDetailPanel;
